import { clearSession, getToken, notifyLogout } from './session';
import { emitDashboardStatus } from '../utils/statusEvents';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const normalizeBaseUrl = (value, fallback) => {
  if (!value || typeof value !== 'string') return fallback;
  try {
    const url = new URL(value);
    return url.toString().replace(/\/+$/u, '');
  } catch {
    return fallback;
  }
};

const DEFAULT_CHAT_BASE = 'https://api.kichat.no';
const CHAT_BASE_URL = normalizeBaseUrl(
  process.env.REACT_APP_CHAT_BASE_URL || process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_API_BASE_URL ? DEFAULT_CHAT_BASE : API_BASE_URL,
);

const DEFAULT_PREVIEW_BASE = 'https://demo.kichat.no';
const PREVIEW_BASE_URL = normalizeBaseUrl(
  process.env.REACT_APP_PREVIEW_URL,
  DEFAULT_PREVIEW_BASE,
);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0)));

const buildChatUrl = (path) => {
  if (!path) return CHAT_BASE_URL;
  return `${CHAT_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const buildChatPreviewUrl = (botId) => {
  if (!botId) return PREVIEW_BASE_URL;
  return `${PREVIEW_BASE_URL}/?bot-id=${encodeURIComponent(botId)}`;
};

const buildChatEmbedSnippet = (botId) => {
  const src = buildChatUrl('/embed.js');
  const id = botId ? botId : '<din bot id>';
  return `<script src="${src}" bot-id="${id}"></script>`;
};

const buildFormEmbedSnippet = (formId) => {
  const src = buildChatUrl('/form-embed.js');
  const id = formId ? formId : '<din form id>';
  return `<script src="${src}" form-id="${id}"></script>`;
};

/**
 * Internal error reporting that bypasses apiFetch to avoid recursion
 * Used within apiFetch for reporting API errors
 */
async function reportClientErrorInternal({ severity = 'error', errorCode, message, meta = {} }) {
  try {
    const authToken = getToken() || null;
    if (!authToken) {
      return;
    }
    await fetch(`${API_BASE_URL}/api/dashboard/client-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ severity, errorCode, message, meta }),
    });
  } catch (err) {
    // Silently fail
    console.warn('[ErrorReporting] Failed to report error:', err.message);
  }
}

const emitGlobalError = (detail) => {
  emitDashboardStatus({
    type: detail.type || 'error',
    title: detail.title || 'Uventet feil',
    message: detail.message,
    source: detail.source || 'API',
    dedupeKey: detail.dedupeKey || `api:${detail.status ?? 'network'}:${detail.message || ''}`,
    durationMs: detail.duration ?? 5_000,
  });
};

function resolveAuthToken() {
  return getToken() || null;
}

async function apiFetch(path, options = {}) {
  const {
    skipAuth = false,
    signal,
    headers: headerOverrides,
    body,
    ...restOptions
  } = options;

  const controller = signal ? null : new AbortController();
  const requestSignal = signal || controller?.signal;

  const headers = new Headers(headerOverrides || {});
  if (!body || !(body instanceof FormData)) {
    headers.set('Accept', 'application/json');
  }
  if (body && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const authToken = skipAuth ? null : resolveAuthToken();
  if (authToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...restOptions,
      body,
      headers,
      signal: requestSignal,
    });
  } catch (networkError) {
    emitGlobalError({
      title: 'API-et svarer ikke',
      message: 'Kunne ikke koble til API-et.',
      status: 0,
      dedupeKey: 'api:network',
    });
    throw new Error('Kunne ikke koble til API-et. Prøv igjen senere.');
  }

  let payload = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    // Handle authentication errors - log out user so they can re-authenticate
    const isAuthError = response.status === 401 
      || (response.status === 403 && (
        payload?.code === 'TOKEN_EXPIRED'
        || payload?.code === 'INVALID_TOKEN'
        || payload?.error === 'Invalid authorization'
      ));
    if (isAuthError) {
      clearSession();
      notifyLogout();
      emitGlobalError({
        title: 'Sesjonen er utløpt',
        message: 'Logg inn på nytt for å fortsette.',
        status: response.status,
        dedupeKey: 'api:auth-expired',
      });
    }
    const message = payload?.error || payload?.message || response.statusText;
    const shouldReportClientError = !isAuthError && response.status !== 404;
    if (response.status >= 500) {
      emitGlobalError({
        title: 'Serverfeil',
        message,
        status: response.status,
        dedupeKey: `api:server:${response.status}:${path}`,
      });

      if (shouldReportClientError) {
        reportClientErrorInternal({
          severity: 'error',
          errorCode: 'SERVER_ERROR',
          message: `API error: ${message}`,
          meta: { path, status: response.status },
        });
      }
    } else if (response.status === 403 && !isAuthError) {
      emitGlobalError({
        title: 'Ingen tilgang',
        message: message || 'Du har ikke rettigheter til denne handlingen.',
        status: response.status,
        dedupeKey: `api:permission:${path}`,
      });
      if (shouldReportClientError) {
        reportClientErrorInternal({
          severity: 'warning',
          errorCode: payload?.code || 'PERMISSION_DENIED',
          message: `API ${response.status}: ${message}`,
          meta: {
            path,
            status: response.status,
            details: payload,
          },
        });
      }
    } else if (response.status >= 400 && response.status < 500 && !isAuthError) {
      // Log 400 errors as warnings (validation errors, bad requests, etc.)
      // These go to master log but don't trigger incidents
      if (shouldReportClientError) {
        reportClientErrorInternal({
          severity: 'warning',
          errorCode: payload?.code || 'CLIENT_ERROR',
          message: `API ${response.status}: ${message}`,
          meta: { 
            path, 
            status: response.status,
            details: payload,
          },
        });
      }
    }
    const error = new Error(message);
    error.status = response.status;
    error.code = payload?.code || null;
    error.details = payload;
    throw error;
  }

  return payload;
}

function toQueryString(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === 'alle') return;
    if (Array.isArray(value)) {
      value
        .filter(
          (item) => item !== undefined && item !== null && item !== '' && item !== 'alle',
        )
        .forEach((item) => {
          search.append(key, item);
        });
      return;
    }
    search.append(key, value);
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

const BILLING_SUMMARY_CACHE = new Map();
const billingSummaryCacheKey = (companyId, { skipStripe = false } = {}) =>
  `${String(companyId || '')}:${skipStripe ? 'fast' : 'full'}`;

const peekBillingSummaryCache = (companyId, { skipStripe = false, maxAgeMs = 30_000 } = {}) => {
  const key = billingSummaryCacheKey(companyId, { skipStripe });
  const entry = BILLING_SUMMARY_CACHE.get(key);
  if (!entry?.value) return null;
  const ageMs = Date.now() - (entry.fetchedAt || 0);
  if (Number.isFinite(maxAgeMs) && maxAgeMs >= 0 && ageMs > maxAgeMs) {
    return null;
  }
  return entry.value;
};

export const DashboardApi = {
  async getOverview(userId, params = {}) {
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/${userId}/overview${query}`);
  },
  async getBots(_accountId, companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/bots`);
  },
  async getCompanyDefaultBotType(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/bot-type-default`);
  },
  async updateCompanyDefaultBotType(companyId, botType) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/bot-type-default`, {
      method: 'PATCH',
      body: JSON.stringify({ bot_type: botType }),
    });
  },
  async createBot(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/bots`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateBot(botId, payload) {
    return apiFetch(`/api/dashboard/bots/${botId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async getBotWebsiteScanStatus(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/website-scan/status`);
  },
  async verifyBotWebsiteInstallation(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/website-scan/verify-installation`, {
      method: 'POST',
    });
  },
  async requestBotWebsiteScan(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/website-scan/request`, {
      method: 'POST',
    });
  },
  async deleteBot(botId) {
    return apiFetch(`/api/dashboard/bots/${botId}`, {
      method: 'DELETE',
    });
  },
  async uploadBotLogo(_userId, botId, file) {
    const form = new FormData();
    form.append('logo', file, file.name);
    return apiFetch(`/api/dashboard/bots/${botId}/logo`, {
      method: 'POST',
      body: form,
      headers: { Accept: 'application/json' },
    });
  },
  async uploadBotLauncherIcon(_userId, botId, file) {
    const form = new FormData();
    form.append('icon', file, file.name);
    return apiFetch(`/api/dashboard/bots/${botId}/launcher-icon`, {
      method: 'POST',
      body: form,
      headers: { Accept: 'application/json' },
    });
  },
  async getBotKnowledge(_userId, botId) {
    return apiFetch(`/api/dashboard/bots/${botId}/knowledge`);
  },
  async saveBotKnowledge(_userId, botId, pairs = []) {
    return apiFetch(`/api/dashboard/bots/${botId}/knowledge`, {
      method: 'PUT',
      body: JSON.stringify({ pairs }),
    });
  },
  async generateBotKnowledge(_userId, botId, text) {
    return apiFetch(`/api/dashboard/bots/${botId}/knowledge/generate`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },
  async getBotProducts(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products`);
  },
  async getCompanyProducts(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/products${query}`);
  },
  async createCompanyProduct(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateCompanyProduct(companyId, productId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async updateCompanyProductInboxConfig(companyId, productId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products/${productId}/inbox-config`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async getCompanyProductCustomerJourney(companyId, productId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products/${productId}/customer-journey`);
  },
  async updateCompanyProductCustomerJourney(companyId, productId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products/${productId}/customer-journey`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async createBotProduct(botId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateBotProduct(botId, productId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async getBotProductInboxConfig(botId, productId) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products/${productId}/inbox-config`);
  },
  async updateBotProductInboxConfig(botId, productId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products/${productId}/inbox-config`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async getBotInquiryTypes(botId, params = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/bots/${botId}/inquiry-types${query}`);
  },
  async getCompanyInquiryTypes(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/inquiry-types${query}`);
  },
  async getCompanyUnlinkedInquiryTypes(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/unlinked-inquiry-types`);
  },
  async resolveCompanyUnlinkedInquiryType(companyId, inquiryTypeId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inquiryTypeId) throw new Error('inquiryTypeId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/unlinked-inquiry-types/${inquiryTypeId}/resolve${query}`);
  },
  async resolveBotInquiryType(botId, inquiryTypeId, params = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!inquiryTypeId) throw new Error('inquiryTypeId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/bots/${botId}/inquiry-types/${inquiryTypeId}/resolve${query}`);
  },
  async createBotInquiryType(botId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/inquiry-types`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async createCompanyInquiryType(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inquiry-types`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateBotInquiryType(botId, inquiryTypeId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!inquiryTypeId) throw new Error('inquiryTypeId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/inquiry-types/${inquiryTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async updateCompanyInquiryType(companyId, inquiryTypeId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inquiryTypeId) throw new Error('inquiryTypeId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inquiry-types/${inquiryTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async deleteBotInquiryType(botId, inquiryTypeId) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!inquiryTypeId) throw new Error('inquiryTypeId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/inquiry-types/${inquiryTypeId}`, {
      method: 'DELETE',
    });
  },
  async deleteCompanyInquiryType(companyId, inquiryTypeId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inquiryTypeId) throw new Error('inquiryTypeId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inquiry-types/${inquiryTypeId}`, {
      method: 'DELETE',
    });
  },
  async getForms(companyId, params = {}) {
    const query = toQueryString({ companyId, ...params });
    return apiFetch(`/api/dashboard/forms${query}`);
  },
  async getForm(formId, companyId) {
    if (!formId) throw new Error('formId er påkrevd');
    const query = toQueryString({ companyId });
    return apiFetch(`/api/dashboard/forms/${formId}${query}`);
  },
  async createForm(companyId, payload = {}) {
    return apiFetch(`/api/dashboard/forms${toQueryString({ companyId })}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateForm(formId, companyId, payload = {}) {
    if (!formId) throw new Error('formId er påkrevd');
    return apiFetch(`/api/dashboard/forms/${formId}${toQueryString({ companyId })}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async deleteForm(formId, companyId) {
    if (!formId) throw new Error('formId er påkrevd');
    return apiFetch(`/api/dashboard/forms/${formId}${toQueryString({ companyId })}`, {
      method: 'DELETE',
    });
  },
  async archiveBotProduct(botId, productId) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products/${productId}/archive`, {
      method: 'POST',
    });
  },
  async restoreBotProduct(botId, productId) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/products/${productId}/restore`, {
      method: 'POST',
    });
  },
  async approveBotProductProposal(botId, proposalId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!proposalId) throw new Error('proposalId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/product-proposals/${proposalId}/approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async rejectBotProductProposal(botId, proposalId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!proposalId) throw new Error('proposalId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/product-proposals/${proposalId}/reject`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async bulkUpdateBotProductProposals(botId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/product-proposals/bulk`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async archiveCompanyProduct(companyId, productId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products/${productId}/archive`, {
      method: 'POST',
    });
  },
  async restoreCompanyProduct(companyId, productId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!productId) throw new Error('productId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/products/${productId}/restore`, {
      method: 'POST',
    });
  },
  async approveCompanyProductProposal(companyId, proposalId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!proposalId) throw new Error('proposalId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/product-proposals/${proposalId}/approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async rejectCompanyProductProposal(companyId, proposalId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!proposalId) throw new Error('proposalId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/product-proposals/${proposalId}/reject`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async bulkUpdateCompanyProductProposals(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/product-proposals/bulk`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  peekBillingSummary(companyId, options = {}) {
    return peekBillingSummaryCache(companyId, options);
  },
  async getBillingSummary(companyId, options = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for fakturering.');
    }
    const {
      skipStripe = false,
      maxAgeMs = 30_000,
      force = false,
      retries = 2,
    } = options || {};

    const cached = !force ? peekBillingSummaryCache(companyId, { skipStripe, maxAgeMs }) : null;
    if (cached) return cached;

    const key = billingSummaryCacheKey(companyId, { skipStripe });
    const existing = BILLING_SUMMARY_CACHE.get(key) || {};
    if (!force && existing.inflight) {
      return existing.inflight;
    }

    const query = skipStripe ? '?skipStripe=1' : '';
    const fetchOnce = () =>
      apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/summary${query}`);

    const inflight = (async () => {
      let attempt = 0;
      // Retry a couple of times on transient gateway errors (common when Stripe is slow).
      // This makes the UI feel less flaky on poor connections.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const payload = await fetchOnce();
          BILLING_SUMMARY_CACHE.set(key, { value: payload, fetchedAt: Date.now(), inflight: null });
          return payload;
        } catch (err) {
          const status = err?.status;
          const shouldRetry =
            attempt < Math.max(0, retries)
            && (status === 502 || status === 503 || status === 504);
          if (!shouldRetry) {
            BILLING_SUMMARY_CACHE.set(key, { value: existing.value || null, fetchedAt: existing.fetchedAt || 0, inflight: null });
            throw err;
          }
          attempt += 1;
          await sleep(350 * attempt);
        }
      }
    })();

    BILLING_SUMMARY_CACHE.set(key, { ...existing, inflight });
    return inflight;
  },
  async partnerInvitePaymentSuccess(companyId) {
    return apiFetch('/api/accounts/partner-invite/payment/success', {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },
  async partnerInvitePaymentCancel(companyId) {
    return apiFetch('/api/accounts/partner-invite/payment/cancel', {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },
  async partnerInvitePaymentStart(companyId) {
    return apiFetch('/api/accounts/partner-invite/payment/start', {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },
  async upgradeBillingPlan(companyId, payload = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for oppgradering.');
    }
    return apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/upgrade`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async enableBillingAddon(companyId, addonCode, payload = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for tillegg.');
    }
    return apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/addons/${encodeURIComponent(addonCode)}/enable`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async disableBillingAddon(companyId, addonCode, payload = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for tillegg.');
    }
    return apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/addons/${encodeURIComponent(addonCode)}/disable`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async startPlanCheckout(companyId, payload = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for betalingen.');
    }
    return apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/checkout`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async openBillingPortal(companyId, payload = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for betalingsoppdatering.');
    }
    return apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/portal`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async cancelPlan(companyId, action = 'cancel') {
    if (!companyId) {
      throw new Error('companyId er påkrevd for kansellering.');
    }
    return apiFetch(`/api/billing/companies/${encodeURIComponent(companyId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({
        companyId,
        action,
      }),
    });
  },
  async getChatHistory(companyId, params = {}, signal) {
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/chat-history${query}`, { signal });
  },
  async getChatMessages(chatId) {
    return apiFetch(`/api/chat-messages/${chatId}`);
  },
  // getMissions removed
  async getCustomers() {
    return apiFetch('/api/dashboard/admin/customers');
  },
  // getForms (legacy) removed - use getFlows instead
  async getBackgroundPresets(params = {}) {
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/background-presets${query}`);
  },
  async getThemePresets(userId) {
    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
    return apiFetch(`/api/dashboard/theme-presets${query}`);
  },
  async createThemePreset(userId, payload) {
    if (!userId) throw new Error('userId er påkrevd');
    return apiFetch(`/api/dashboard/${userId}/theme-presets`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async deleteThemePreset(userId, presetId) {
    if (!userId) throw new Error('userId er påkrevd');
    if (!presetId) throw new Error('presetId er påkrevd');
    return apiFetch(`/api/dashboard/${userId}/theme-presets/${presetId}`, {
      method: 'DELETE',
    });
  },
  // saveForms (legacy), getFormAssignments, saveFormAssignments removed - use saveFlows, getFlowAssignments, saveFlowAssignments instead
  async getFlowAssignments(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/flows/assignments`);
  },
  async saveFlowAssignments(companyId, payload) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/flows/assignments`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async getFlows(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/flows`);
  },
  async saveFlows(companyId, payload) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/flows`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async deleteFlow(companyId, flowId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!flowId) throw new Error('flowId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/flows/${flowId}`, {
      method: 'DELETE',
    });
  },
  async draftFlow(companyId, payload) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/flows/draft`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  },
  async getTools(companyId, options = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString({
      botId: options?.botId || undefined,
    });
    return apiFetch(`/api/dashboard/companies/${companyId}/tools${query}`);
  },
  // User account profile (personal info)
  async getUserProfile() {
    return apiFetch('/api/accounts/profile');
  },
  async updateUserProfile(payload = {}) {
    return apiFetch('/api/accounts/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async getNotificationSettings() {
    return apiFetch('/api/accounts/profile/notifications');
  },
  async updateNotificationSettings(payload = {}) {
    return apiFetch('/api/accounts/profile/notifications', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  // Company billing profile
  async getCustomerProfile(companyId) {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return apiFetch(`/api/dashboard/customer/profile${query}`);
  },
  async getCustomerProfileStatus(companyId) {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return apiFetch(`/api/dashboard/customer/profile/status${query}`);
  },
  async updateCustomerProfile(companyId, payload = {}) {
    if (!companyId) {
      throw new Error('companyId er påkrevd for å oppdatere kundeprofilen.');
    }
    return apiFetch('/api/dashboard/customer/profile', {
      method: 'PATCH',
      body: JSON.stringify({
        ...payload,
        companyId,
      }),
    });
  },
  async getBookingSchedules(userId) {
    return apiFetch(`/api/dashboard/${userId}/booking-schedules`);
  },
  async createBookingSchedule(userId, payload = {}) {
    return apiFetch(`/api/dashboard/${userId}/booking-schedules`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateBookingSchedule(scheduleId, payload = {}) {
    return apiFetch(`/api/dashboard/booking-schedules/${scheduleId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async deleteBookingSchedule(scheduleId) {
    return apiFetch(`/api/dashboard/booking-schedules/${scheduleId}`, {
      method: 'DELETE',
    });
  },
  async getAccountContext() {
    return apiFetch('/api/accounts/context');
  },
  async acceptPartnerInvite(inviteToken) {
    const token = typeof inviteToken === 'string' ? inviteToken.trim() : '';
    if (!token) throw new Error('inviteToken er påkrevd');
    return apiFetch('/api/accounts/partner-invite/accept', {
      method: 'POST',
      body: JSON.stringify({ inviteToken: token }),
    });
  },
  async getPendingPartnerInvite() {
    return apiFetch('/api/accounts/pending-partner-invite');
  },
  async selectCompany(companyId) {
    return apiFetch('/api/accounts/companies/select', {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },
  async createCompany(payload = {}) {
    return apiFetch('/api/accounts/companies', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async archiveCompany(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/accounts/companies/${encodeURIComponent(companyId)}/archive`, {
      method: 'POST',
    });
  },
  async getBillingPlans() {
    return apiFetch('/api/billing/plans');
  },
  async getBillingAddons(companyId = null) {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    return apiFetch(`/api/billing/addons${query}`);
  },
  async getCompanyMembers(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/members`);
  },
  async inviteCompanyMember(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/members`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async getCompanySummary(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/summary`);
  },
  async updateCompanyMember(companyId, memberId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!memberId) throw new Error('memberId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async updateCompanyMemberGroups(companyId, memberId, groupIds = []) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!memberId) throw new Error('memberId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/members/${memberId}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ groupIds }),
    });
  },
  async transferCompanyOwnership(companyId, memberId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!memberId) throw new Error('memberId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/members/${memberId}/transfer-ownership`, {
      method: 'POST',
    });
  },
  async removeCompanyMember(companyId, memberId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!memberId) throw new Error('memberId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/members/${memberId}`, {
      method: 'DELETE',
    });
  },
  async getCompanyAccess(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access`);
  },
  async createPermissionGroup(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access/groups`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updatePermissionGroup(companyId, groupId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!groupId) throw new Error('groupId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access/groups/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async deletePermissionGroup(companyId, groupId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!groupId) throw new Error('groupId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access/groups/${groupId}`, {
      method: 'DELETE',
    });
  },
  async updatePermissionGroupMembers(companyId, groupId, memberIds = []) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!groupId) throw new Error('groupId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access/groups/${groupId}/members`, {
      method: 'PUT',
      body: JSON.stringify({ memberIds }),
    });
  },
  async updatePermissionGroupPermissions(companyId, groupId, permissionRules = []) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!groupId) throw new Error('groupId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access/groups/${groupId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissionRules }),
    });
  },
  async updatePermissionGroupProducts(companyId, groupId, productScope = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!groupId) throw new Error('groupId er påkrevd');
    return apiFetch(`/api/accounts/companies/${companyId}/access/groups/${groupId}/products`, {
      method: 'PUT',
      body: JSON.stringify(productScope),
    });
  },
  // Super-admin: List all companies
  async getAdminCompanies(params = {}) {
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/admin/companies${query}`);
  },
  // Super-admin: Get single company details
  async getAdminCompany(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/admin/companies/${companyId}`);
  },
  // Super-admin: Enter/impersonate a company
  async enterAdminCompany(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/admin/companies/${companyId}/enter`, {
      method: 'POST',
    });
  },

  // =====================================================================
  // LIVE AGENT API
  // =====================================================================

  // Agent availability
  async getAgentAvailability(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/agent/availability?companyId=${companyId}`);
  },
  async setAgentAvailability(companyId, isAvailable) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch('/api/agent/availability', {
      method: 'POST',
      body: JSON.stringify({ companyId, isAvailable }),
    });
  },
  async updateAgentSettings(companyId, settings) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch('/api/agent/settings', {
      method: 'PATCH',
      body: JSON.stringify({ companyId, ...settings }),
    });
  },

  // Agent queue
  async getAgentQueue(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/agent/queue/${companyId}`);
  },
  async getAgentMyChats(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/agent/my-chats?companyId=${companyId}`);
  },
  async getOnlineAgents(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/agent/online/${companyId}`);
  },

  // Chat offer handling
  async acceptChatOffer(chatId) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/accept/${chatId}`, { method: 'POST' });
  },
  async declineChatOffer(chatId, companyId) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/decline/${chatId}`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },

  // Chat interaction
  async getAgentChatMessages(chatId, limit = 100) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/chat/${chatId}/messages?limit=${limit}`);
  },
  async sendAgentMessage(chatId, message) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/chat/${chatId}/message`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },
  async sendAgentTyping(chatId, isTyping) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/chat/${chatId}/typing`, {
      method: 'POST',
      body: JSON.stringify({ isTyping }),
    });
  },

  // Chat lifecycle
  async handBackToAI(chatId) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/chat/${chatId}/handback`, { method: 'POST' });
  },
  async resolveChat(chatId) {
    if (!chatId) throw new Error('chatId er påkrevd');
    return apiFetch(`/api/agent/chat/${chatId}/resolve`, { method: 'POST' });
  },

  // Agent metrics
  async getAgentMetrics(companyId, days = 7) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/agent/metrics/${companyId}?days=${days}`);
  },

  // =====================================================================
  // SUPPORT SCHEDULES API
  // =====================================================================

  // Company schedules
  async getCompanySchedules(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/schedules`);
  },
  async createSchedule(companyId, scheduleData) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/schedules`, {
      method: 'POST',
      body: JSON.stringify(scheduleData),
    });
  },
  async updateSchedule(scheduleId, updates) {
    if (!scheduleId) throw new Error('scheduleId er påkrevd');
    return apiFetch(`/api/dashboard/schedules/${scheduleId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },
  async deleteSchedule(scheduleId) {
    if (!scheduleId) throw new Error('scheduleId er påkrevd');
    return apiFetch(`/api/dashboard/schedules/${scheduleId}`, {
      method: 'DELETE',
    });
  },
  async updateScheduleWindows(scheduleId, windows) {
    if (!scheduleId) throw new Error('scheduleId er påkrevd');
    return apiFetch(`/api/dashboard/schedules/${scheduleId}/windows`, {
      method: 'PUT',
      body: JSON.stringify({ windows }),
    });
  },
  async addScheduleException(scheduleId, exception) {
    if (!scheduleId) throw new Error('scheduleId er påkrevd');
    return apiFetch(`/api/dashboard/schedules/${scheduleId}/exceptions`, {
      method: 'POST',
      body: JSON.stringify(exception),
    });
  },
  async removeScheduleException(exceptionId) {
    if (!exceptionId) throw new Error('exceptionId er påkrevd');
    return apiFetch(`/api/dashboard/schedules/exceptions/${exceptionId}`, {
      method: 'DELETE',
    });
  },

  // Bot schedule assignments
  async getBotSchedules(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/schedules`);
  },
  async assignScheduleToBot(botId, scheduleId, priority = 0) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!scheduleId) throw new Error('scheduleId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/schedules`, {
      method: 'POST',
      body: JSON.stringify({ scheduleId, priority }),
    });
  },
  async unassignScheduleFromBot(botId, scheduleId) {
    if (!botId) throw new Error('botId er påkrevd');
    if (!scheduleId) throw new Error('scheduleId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/schedules/${scheduleId}`, {
      method: 'DELETE',
    });
  },

  // Bot support mode configuration
  async getBot(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/full`);
  },
  async updateBotSupportConfig(botId, { supportMode, escalationConfig }) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/support-config`, {
      method: 'PATCH',
      body: JSON.stringify({ supportMode, escalationConfig }),
    });
  },
  // Interactive week schedule configuration (new system)
  async updateBotScheduleConfig(botId, { supportMode, weekSchedule, escalationPolicy, escalationConfig, conversationPolicy }) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/schedule-config`, {
      method: 'PATCH',
      body: JSON.stringify({ supportMode, weekSchedule, escalationPolicy, escalationConfig, conversationPolicy }),
    });
  },

  // =====================================================================
  // USER PROFILE API
  // =====================================================================

  async uploadProfilePicture(file) {
    if (!file) throw new Error('file er påkrevd');
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch('/api/accounts/profile/picture', {
      method: 'POST',
      body: formData,
    });
  },
  async removeProfilePicture() {
    return apiFetch('/api/accounts/profile/picture', {
      method: 'DELETE',
    });
  },

  async getBotTabsConfig(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/tabs-config`);
  },
  async updateBotTabsConfig(botId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/tabs-config`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async testBotTabsConfig(botId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/tabs-config/test`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async getBotTabsLessonOptions(botId, payload = {}) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/tabs-config/lesson-options`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async deleteBotTabsConfig(botId) {
    if (!botId) throw new Error('botId er påkrevd');
    return apiFetch(`/api/dashboard/bots/${botId}/tabs-config`, {
      method: 'DELETE',
    });
  },
  async addCompanyContactSubmissionNote(companyId, submissionId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!submissionId) throw new Error('submissionId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/contact-submissions/${submissionId}/notes`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async getCompanyInbox(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox${query}`);
  },
  async getCompanyInboxTasks(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/tasks${query}`);
  },
  async getCompanyInboxOverview(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/overview${query}`);
  },
  async getCompanyInboxSummary(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/summary${query}`);
  },
  async getCompanyInboxColumns(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/columns`);
  },
  async updateCompanyInboxColumns(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/columns`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async getCompanyInboxDetail(companyId, inboxId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}`);
  },
  async updateCompanyInbox(companyId, inboxId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async deleteCompanyInbox(companyId, inboxId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}`, {
      method: 'DELETE',
    });
  },
  async createCompanyInbox(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async addCompanyInboxNote(companyId, inboxId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}/notes`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async getCompanyInboxCalendar(companyId, params = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    const query = toQueryString(params);
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/calendar${query}`);
  },
  async getCompanyInboxCalendarFeeds(companyId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/calendar/feeds`);
  },
  async createCompanyInboxCalendarFeed(companyId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/calendar/feeds`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateCompanyInboxCalendarFeed(companyId, feedId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!feedId) throw new Error('feedId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/calendar/feeds/${feedId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async rotateCompanyInboxCalendarFeed(companyId, feedId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!feedId) throw new Error('feedId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/calendar/feeds/${feedId}/rotate`, {
      method: 'POST',
    });
  },
  async deleteCompanyInboxCalendarFeed(companyId, feedId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!feedId) throw new Error('feedId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/calendar/feeds/${feedId}`, {
      method: 'DELETE',
    });
  },
  async updateCompanyInboxProgression(companyId, inboxId, stepKey, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    if (!stepKey) throw new Error('stepKey er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}/progression/${encodeURIComponent(stepKey)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async createCompanyInboxProgressionLog(companyId, inboxId, stepKey, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    if (!stepKey) throw new Error('stepKey er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}/progression/${encodeURIComponent(stepKey)}/logs`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  async updateCompanyInboxProgressionLog(companyId, inboxId, stepKey, logId, payload = {}) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    if (!stepKey) throw new Error('stepKey er påkrevd');
    if (!logId) throw new Error('logId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}/progression/${encodeURIComponent(stepKey)}/logs/${logId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  async deleteCompanyInboxProgressionLog(companyId, inboxId, stepKey, logId) {
    if (!companyId) throw new Error('companyId er påkrevd');
    if (!inboxId) throw new Error('inboxId er påkrevd');
    if (!stepKey) throw new Error('stepKey er påkrevd');
    if (!logId) throw new Error('logId er påkrevd');
    return apiFetch(`/api/dashboard/companies/${companyId}/inbox/${inboxId}/progression/${encodeURIComponent(stepKey)}/logs/${logId}`, {
      method: 'DELETE',
    });
  },

  // ---- Google Workspace Integration ----

  async getGoogleServiceAccount(companyId) {
    return apiFetch(
      `/api/dashboard/companies/${encodeURIComponent(companyId)}/google/service-account`,
    );
  },

  async saveGoogleServiceAccount(companyId, { client_email, private_key }) {
    return apiFetch(
      `/api/dashboard/companies/${encodeURIComponent(companyId)}/google/service-account`,
      {
        method: 'PUT',
        body: JSON.stringify({ client_email, private_key }),
      },
    );
  },

  async deleteGoogleServiceAccount(companyId) {
    return apiFetch(
      `/api/dashboard/companies/${encodeURIComponent(companyId)}/google/service-account`,
      { method: 'DELETE' },
    );
  },

  async verifyGoogleWorkspaceEmail(companyId, email) {
    return apiFetch(
      `/api/dashboard/companies/${encodeURIComponent(companyId)}/google/verify-email`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
    );
  },

  async getMicrosoftStatus() {
    return apiFetch('/api/dashboard/microsoft/status');
  },

  async disconnectMicrosoft() {
    return apiFetch('/api/dashboard/microsoft/disconnect', { method: 'DELETE' });
  },

  async getChangelog() {
    return apiFetch('/api/dashboard/changelog');
  },

  async markChangelogSeen(entryId) {
    if (!entryId) throw new Error('entryId er påkrevd');
    return apiFetch(`/api/dashboard/changelog/${encodeURIComponent(entryId)}/seen`, { method: 'POST' });
  },

  async markChangelogSeenBulk(ids) {
    if (!Array.isArray(ids) || ids.length === 0) return null;
    return apiFetch('/api/dashboard/changelog/seen', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  },
};

// ============================================================================
// ERROR REPORTING TO METRIKK
// ============================================================================

/**
 * Report a client-side error to the server for incident tracking
 * @param {Object} options - Error options
 * @param {'critical' | 'error' | 'warning'} [options.severity='error'] - Severity
 * @param {string} [options.errorCode] - Error code (e.g., 'AUTH_ERROR', 'NETWORK_ERROR')
 * @param {string} options.message - Error message
 * @param {Object} [options.meta] - Additional metadata
 */
async function reportClientError({ severity = 'error', errorCode, message, meta = {} }) {
  try {
    await apiFetch('/api/dashboard/client-error', {
      method: 'POST',
      body: JSON.stringify({ severity, errorCode, message, meta }),
    });
  } catch (err) {
    // Silently fail - don't crash on error reporting failures
    console.warn('[ErrorReporting] Failed to report error:', err.message);
  }
}

export const DashboardConfig = {
  apiBaseUrl: API_BASE_URL,
  chatBaseUrl: CHAT_BASE_URL,
  googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  getChatEmbedUrl: () => buildChatUrl('/embed.js'),
  getChatPreviewUrl: buildChatPreviewUrl,
  getChatEmbedSnippet: buildChatEmbedSnippet,
  getFormEmbedSnippet: buildFormEmbedSnippet,
  getAuthToken: resolveAuthToken, // For SSE/EventSource which can't send headers
};

export { reportClientError };
